import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { FinancialTransactionFormValues } from "gsintegrations";
import {
  incomingRepository,
  type FinancialTransactionWithTerreiroId,
} from "../repositories/incoming.repository";
import { financialQueryKeys } from "./financialQueryKeys";
import { invalidateAccountBalanceQueries } from "./invalidateAccountBalanceQueries";

/**
 * Mutation hook to create an incoming transaction (entrada).
 * Invalidates relevant financial queries on settle.
 * Supports both terreiro and medium contexts.
 */
export function useCreateIncomingMutation(
  terreiroId?: string,
  mediumId?: string
) {
  const qc = useQueryClient();

  return useMutation<unknown, Error, FinancialTransactionFormValues>({
    mutationKey: ["incoming", terreiroId, mediumId, "create"],
    mutationFn: (payload) =>
      incomingRepository.createIncoming(
        payload as FinancialTransactionWithTerreiroId
      ),
    retry: 0,
    onSettled: () => {
      const promises: Promise<unknown>[] = [
        qc.invalidateQueries({
          queryKey: financialQueryKeys.currentMonthIncome(terreiroId, mediumId),
        }),
        invalidateAccountBalanceQueries(qc, { terreiroId, mediumId }),
      ];
      if (terreiroId) {
        promises.push(
          qc.invalidateQueries({
            queryKey: financialQueryKeys.monthlyFees(terreiroId),
          })
        );
      }
      // return the promise so React Query keeps mutation pending until refetch completes
      return Promise.all(promises);
    },
  });
}
